import { Button, Col, ColorPicker, Divider, Form, Input, Modal, Row, Select, Tabs, notification } from "antd";

import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Language, TagTableData, TagContentFormData, TagContent } from "../../models/countries-interface";
import { TagApi } from "../../apis/tag-api";
import { Formik, FormikProps, FormikTouched } from "formik";
import * as Yup from "yup";
import { BoxTag } from "../../components/box-tag/BoxTag";
import { AppContext } from "../../context/app-context";

interface EditProps {
  onExit: () => void;
  setEditingId: Dispatch<SetStateAction<string | null>>;
  existingTagProp: TagTableData | null;
}

interface SelectOption {
  key: number;
  label: string;
  value: string;
}

const EditTagPage = ({ onExit, setEditingId, existingTagProp }: EditProps) => {
  const formRef = useRef<FormikProps<TagContentFormData>>(null);
  const { selectedCountry, profile } = useContext(AppContext);

  if (!selectedCountry) {
	onExit();
  }

  const country = selectedCountry!;
  const languages = country.languages.iv;
  const [api, contextHolder] = notification.useNotification();

  const [existingTag, setExistingTag] = useState<TagTableData | null>(existingTagProp);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(country.languages.iv[0]);
  const [tagTypes, setTagTypes] = useState<SelectOption[]>([]);
  const [iconOptions, setIconOptions] = useState<SelectOption[]>([]);
  const [initialTagForm, setInitialTagForm] = useState<TagContentFormData>({
	Title: {},
	Type: null,
	ColorCode: null,
	BorderColorCode: null,
	Icon: null,
  });
  const [tagForm, setTagForm] = useState<TagContentFormData>();

  const [isConfirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [isLoadingTagTypes, setIsLoadingTagTypes] = useState<boolean>(false);
  const [isLoadingIconOptions, setIsLoadingIconOptions] = useState<boolean>(false);
  const [isSubmitDisabled, setSubmitDisabled] = useState<boolean>(false);

  const [, modalContextHolder] = Modal.useModal();

  const openError = () => {
    api.error({
      message: `Error: Encountered an error ${existingTag ? 'updating' : 'creating'} this filter.`,
      description: null,
      placement: "top",
    });
  };

  const openSuccess = () => {
    api.success({
      message: "Changes saved",
      description: null,
      placement: "top",
    });
  };

  useEffect(() => {
	if (existingTag && existingTag.SupportedCountries?.iv?.length > 1) {
	  setSubmitDisabled(true);
	} else {
	  setSubmitDisabled(false);
	}
  }, [existingTag]);

  const loadTagTypes = useCallback(() => {
	if (tagTypes.length > 0 || isLoadingTagTypes === true) {
	  return;
	}

	setIsLoadingTagTypes(true);

	TagApi.getTagTypes()
	  .then((tagTypes: string[]) => {
		var workingTagTypes: SelectOption[] = [];
		tagTypes.forEach((tagType, index) => { workingTagTypes.push({ key: index + 1, label: tagType, value: tagType })});
		setTagTypes(workingTagTypes); 
		setIsLoadingTagTypes(false); 
	  });
  }, [tagTypes.length, isLoadingTagTypes]);

  const loadIconOptions = useCallback(() => {
	if (iconOptions.length > 0 || isLoadingIconOptions === true) {
	  return;
	}

	setIsLoadingIconOptions(true);

	TagApi.getTagIcons()
	  .then((iconOptions: string[]) => {
		var workingIconOptions: SelectOption[] = [{ key: 0, label: "", value: "" }];
		iconOptions.forEach((iconOption, index) => { workingIconOptions.push({ key: index + 1, label: iconOption, value: iconOption })});
		setIconOptions(workingIconOptions); 
		setIsLoadingIconOptions(false); 
	  })
	  .catch((ex) => { console.log("Error:", ex); });
  }, [iconOptions.length, isLoadingIconOptions]);

  useEffect(() => {
	loadTagTypes();
	loadIconOptions();
  }, [loadTagTypes, loadIconOptions]);

  const updateTagForm = useCallback((workingExistingTag?: TagTableData) => {
	const tag = workingExistingTag ?? existingTag;

	var workingTitle: {[key: string]: string | null} = {};
	languages.forEach((language) => { 
	  workingTitle[language.code] = tag ? tag.Title[language.code] : null
	});

	var workingTagForm: TagContentFormData = {
	  Title: workingTitle,
	  Type: tag ? tag.Type?.iv : null,
	  ColorCode: tag ? tag.ColorCode?.iv : null,
	  BorderColorCode: tag ? tag.BorderColorCode?.iv : null,
	  Icon: tag ? tag.Icon?.iv : null,
	};
	setInitialTagForm(workingTagForm);
	setTagForm(workingTagForm);
  }, [existingTag, languages]);

  useEffect(() => {
	if (initialTagForm && Object.keys(initialTagForm).length > 0 && tagForm) {
	  return;
	}

	updateTagForm();
  }, [languages, initialTagForm, tagForm, existingTag, updateTagForm]);

  const onChangeTab = (key?: string) => {
    let lang = languages.find(l => l.code === key)
	if (!lang) {
	  throw Error("Bad Language Selected.");
	}
	if (lang !== selectedLanguage && lang) setSelectedLanguage(lang);
  }

  const submitAndSave = async (values: TagContentFormData) => {
	setSubmitting(true);

	const tagRequest = {
	  ...values,
	  SupportedCountries: existingTag ? [existingTag.SupportedCountries?.iv?.[0].Id ?? existingTag.SupportedCountries?.iv?.[0]] : [country.id],
	};

	if (existingTag?.Id) {
	  await TagApi.updateTag(existingTag.Id, tagRequest)
	    .then(() => {
	      openSuccess();
	    })
	    .catch(() => {
		  openError();
		});
	}
	else {
	  await TagApi.createTag(tagRequest)
	    .then((result: TagContent) => {
	      openSuccess();
		  setEditingId(result.id);
		  const workingExistingTag = {
			Id: result.id,
			...result.data,
		  };
		  setExistingTag(workingExistingTag);
		  updateTagForm(workingExistingTag);
	    })
	    .catch(() => {
		  openError();
		});
	}
	
	setSubmitting(false);
  };

  const setAllTouched = () => {
	if (!formRef.current) {
	  return;
	}

	var workingTouched: FormikTouched<TagContentFormData> = Object.keys({ ...formRef.current.values }).reduce(
      (acc, val) => ({
	    ...acc,
		[val]: true,
	  }),
	  {}
	);

	workingTouched.Title = languages.map(x => x.code).reduce(
      (acc, val) => ({
	    ...acc,
		[val]: true,
	  }),
	  {}
	);

	formRef.current.setTouched(workingTouched);
  };
  
  if (!profile || !profile.TagsManagementEnabled) {
	return null;
  }

  return (
    <>
      {contextHolder}
	  <div className="content-header edit-header">
		<Button
	      icon={<FontAwesomeIcon icon={['far', 'angle-left']} />}
		  aria-label="Back to Filters"
		  onClick={onExit}
		  className="page-title"
		  type="text"
		>
		  {'Back to Filters'}
		</Button>
		<Button
		  aria-label='Cancel'
		  onClick={onExit}
		  className="row-right"
		  type="default"
		>
		  {'Cancel'}
		</Button>
		<Button
		  icon={<FontAwesomeIcon icon={['far', 'floppy-disk']} />}
		  aria-label='Save Changes'
		  onClick={() => { 
			formRef.current?.validateForm()
			  .then(errors => {
				setAllTouched();

				if (Object.keys(errors).length === 0) {
				  setConfirmOpen(true);
				}
			  });
		  }}
		  type="primary"
		  loading={isSubmitting}
		  disabled={isSubmitDisabled}
		>
		  {'Save Changes'}
		</Button>
		<Modal
		  title="Confirmation"
		  okText="Confirm"
		  onOk={() => {
		    setConfirmOpen(false);
			formRef.current?.handleSubmit();
		  }}
		  onCancel={() => { setConfirmOpen(false) }}
		  open={isConfirmOpen}
		>
		  <p>
		    Please confirm that you would like to save these changes.
			By clicking save you are updating the live Bright Sky systems.
			Please ensure all information you have added is accurate and correct.
		  </p>
	    </Modal>
		{modalContextHolder}
	  </div>
	  <div className='content-body'>
		<Tabs 
		  className="tag-tabs" 
		  defaultActiveKey="1" 
		  items={languages.map((l) => ({ key: l.code, label: l.language }))} 
		  onChange={(k) => { onChangeTab(k); }} 
		/>		
		<Formik
		  enableReinitialize
		  initialValues={initialTagForm}
		  onSubmit={(values: TagContentFormData) => submitAndSave(values)}
		  validationSchema={Yup.object({
			Title: Yup.lazy((titleObj) => {
			  if (titleObj) {
				const newEntries = languages.map(x => x.code).reduce(
				  (acc, val) => ({
					...acc,
					[val]: Yup.string()
							  .typeError("Title should be a string.")
							  .min(1, `Title is required for ${languages.find(x => x.code === val)?.language}.`)
							  .required(`Title is required for ${languages.find(x => x.code === val)?.language}.`),
				  }),
				  {}
				);

				return Yup.object().shape(newEntries)
			  }
			  return Yup.mixed().notRequired()
			}),
			Type: Yup.string().min(1, "A Type is required.").typeError("Type should be a string.").required("A Type is required."),
			ColorCode: Yup.string().nullable().typeError("Color code should be a string."),
			BorderColorCode: Yup.string().nullable().typeError("Border color code should be a string."),
			Icon: Yup.string().nullable().typeError("Icon should be a string."),
		  })}
		  innerRef={formRef}
		>
		  {({ handleChange, handleBlur, values, setFieldValue, touched, errors, setTouched }) => {

			const titleTouched = touched.Title?.[selectedLanguage.code];
			const titleError = errors.Title?.[selectedLanguage.code];

			return (
			  <Form className={"edit-tag-form"}>
				<div className='localized content-tile'>
				  <Form.Item
					validateStatus={titleTouched && titleError ? "error" : ""}
					help={titleError}
					required
					label={
					  <h3>{'Title'}</h3>
					}
				  >
					<Input
					  id="Title"
					  name={`Title.${selectedLanguage.code}`}
					  placeholder="Filter Title"
					  onBlur={() => {
						var workingTouched = { ...touched };
						var workingTitleTouched: {[lang: string]: boolean} = { ...touched.Title as object };
						workingTitleTouched[selectedLanguage.code] = true;
						workingTouched.Title = workingTitleTouched;
						setTouched(workingTouched);
					  }}
					  value={values?.Title?.[selectedLanguage.code] ?? ""}
					  onChange={handleChange}
					  style={{ minWidth: '300px' }}
					  disabled={isSubmitDisabled}
					/>
				  </Form.Item>
				</div>
				<Divider />
				<div className='content-tile'>
				  <Row>
			  	    <Col className="invariant-form-column">
				      <Form.Item
					    validateStatus={touched.Type && errors.Type ? "error" : ""}
					    help={errors.Type}
					    required
					    label={
					      <h3>{'Type'}</h3>
					    }
					    style={{ marginBottom: '35px' }}
			          >
					    <Select
					      id="Type"
					      placeholder="Filter Type"
					      onBlur={handleBlur}
					      value={values?.Type}
					      options={tagTypes}
					      loading={isLoadingTagTypes}
					      onChange={(e) => setFieldValue("Type", e, true)}
					      style={{ minWidth: '300px' }}
					      disabled={isSubmitDisabled}
					    />
				      </Form.Item>
				      <Form.Item
					    validateStatus={touched.ColorCode && errors.ColorCode ? "error" : ""}
					    help={errors.ColorCode}
					    label={
					      <h3>{'Filter Color'}</h3>
					    }
			          >
					    <ColorPicker
					      defaultValue={existingTag?.ColorCode?.iv}
					      showText
					      onChange={(color) => setFieldValue("ColorCode", color.toRgbString(), true)}
					      disabled={isSubmitDisabled}
						  defaultFormat={"rgb"}
					    />
				      </Form.Item>
				      <Form.Item
					    validateStatus={touched.BorderColorCode && errors.BorderColorCode ? "error" : ""}
					    help={errors.BorderColorCode}
					    label={
					      <h3>{'Filter Border Color'}</h3>
					    }
			          >
					    <ColorPicker
					      defaultValue={existingTag?.BorderColorCode?.iv}
					      showText
					      onChange={(color) => setFieldValue("BorderColorCode", color.toRgbString(), true)}
					      disabled={isSubmitDisabled}
						  defaultFormat={"rgb"}
					    />
				      </Form.Item>
				 {/* <Form.Item*/}
					{/*validateStatus={touched.Icon && errors.Icon ? "error" : ""}*/}
					{/*help={errors.Icon}*/}
					{/*label={*/}
					{/*  <h3>{'Icon'}</h3>*/}
					{/*}*/}
			  {/*    >*/}
					{/*<Select*/}
					{/*  id="Icon"*/}
					{/*  placeholder="Tag Icon"*/}
					{/*  onBlur={handleBlur}*/}
					{/*  value={values?.Icon}*/}
					{/*  options={iconOptions}*/}
					{/*  loading={isLoadingIconOptions}*/}
					{/*  onChange={(e) => setFieldValue("Icon", e, true)}*/}
					{/*  style={{ minWidth: '300px' }}*/}
					{/*/>*/}
				 {/* </Form.Item>*/}
				  </Col>
				  <Col className="tag-preview-column">
				    <div className="tag-preview-box">
					  <h3>{"Preview"}</h3>
					  <BoxTag
					    text={values.Title[selectedLanguage.code] ?? "Preview"}
						color={values.ColorCode}
						borderColor={values.BorderColorCode}
					  />
					</div>
				  </Col>
				  </Row>
				</div>
			  </Form>
			);
		  }}
		</Formik>
      </div>
	</>
  );
}

export default EditTagPage;
